import { useEffect, useState, ChangeEvent } from 'react'
import { useI18n } from '../i18n'
import { applyTheme } from '../utils/theme'
import { apiPost, apiGet, apiPut } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'

type Contact = { id: string; type: 'email'|'telegram'|'sms'; value: string }
type Ticket = { id: number; subject: string; status: string; created_at: string }
type MonitoringConfig = {
  config_id: number
  enabled: boolean
  updated_at?: string
  prometheus: {
    enabled: boolean
    pushgateway_url?: string | null
    remote_write_url?: string | null
    job_name?: string | null
    basic_auth_user?: string | null
    basic_auth_password_set: boolean
  }
  elk: {
    enabled: boolean
    endpoint_url?: string | null
    index?: string | null
    username?: string | null
    password_set: boolean
  }
}

export default function Settings() {
  const { lang, setLang, t } = useI18n()
  const [theme, setTheme] = useState<string>((localStorage.getItem('theme') as string) || 'auto')
  const [me, setMe] = useState<any>(null)
  const [contacts, setContacts] = useState<Contact[]>([])
  const { current } = useTenant()
  const [cType, setCType] = useState<'email'|'telegram'|'sms'>('email')
  const [cValue, setCValue] = useState('')
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState('')
  const [ticketContact, setTicketContact] = useState('')
  const [info, setInfo] = useState<string>('')
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [appVersion, setAppVersion] = useState<string>('')
  const [monitoring, setMonitoring] = useState<MonitoringConfig | null>(null)
  const [monitoringStatus, setMonitoringStatus] = useState('')
  const [monitoringSaving, setMonitoringSaving] = useState(false)
  const [promPassword, setPromPassword] = useState('')
  const [elkPassword, setElkPassword] = useState('')
  const [clearPromPassword, setClearPromPassword] = useState(false)
  const [clearElkPassword, setClearElkPassword] = useState(false)

  const updateMonitoringState = (mutator: (current: MonitoringConfig) => MonitoringConfig) => {
    setMonitoring((prev: MonitoringConfig | null) => (prev ? mutator(prev) : prev))
  }

  const onMonitoringCheckbox = (mutator: (checked: boolean, current: MonitoringConfig) => MonitoringConfig) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked
      updateMonitoringState(current => mutator(checked, current))
    }
  }

  const onMonitoringInput = (mutator: (value: string, current: MonitoringConfig) => MonitoringConfig) => {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      updateMonitoringState(current => mutator(value, current))
    }
  }

  useEffect(() => {
    (async () => {
      try { setMe(await apiGet('/me')) } catch {}
      try {
        const info = await apiGet('/about')
        if (info?.version) setAppVersion(info.version as string)
      } catch {}
    })()
  }, [])

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiGet('/monitoring/config')
        setMonitoring(cfg)
        setMonitoringStatus('')
      } catch (e: any) {
        setMonitoringStatus(e?.message || 'Impossibile caricare la configurazione di monitoraggio')
      }
    })()
  }, [])

  useEffect(() => {
    (async () => {
      const tenantId = current?.id
      if (tenantId) {
        try { setContacts(await apiGet(`/tenants/${tenantId}/notify-contacts`)) } catch {}
      } else {
        try { const saved = localStorage.getItem('notify_contacts'); if (saved) setContacts(JSON.parse(saved)) } catch {}
      }
      try { setTickets(await apiGet('/support/tickets')) } catch {}
    })()
  }, [current?.id])

  const saveContactsLocal = (list: Contact[]) => {
    setContacts(list)
    localStorage.setItem('notify_contacts', JSON.stringify(list))
  }

  const addContact = async () => {
    if (!cValue.trim()) return
    const tenantId = current?.id
    if (tenantId) {
      const created = await apiPost(`/tenants/${tenantId}/notify-contacts`, { type: cType, value: cValue.trim(), active: true })
      setContacts([...(contacts||[]), created])
    } else {
      const item: Contact = { id: String(Date.now()), type: cType, value: cValue.trim() }
      saveContactsLocal([...(contacts||[]), item])
    }
    setCValue('')
  }
  const delContact = async (id: string) => {
    const tenantId = current?.id
    if (tenantId) {
      await fetch(`/tenants/${tenantId}/notify-contacts/${id}`, { method: 'DELETE', credentials: 'include' })
  setContacts((contacts||[]).filter((c: Contact) => String((c as any).id) !== String(id)))
    } else {
  saveContactsLocal((contacts||[]).filter((c: Contact) => c.id !== id))
    }
  }

  const onThemeChange = (val: string) => { setTheme(val); applyTheme(val as any) }

  const submitTicket = async () => {
    try {
      await apiPost('/support/ticket', { subject: ticketSubject, message: ticketMessage, contact: ticketContact })
      setInfo('Ticket inviato')
      setTicketSubject(''); setTicketMessage(''); setTicketContact('')
      try { setTickets(await apiGet('/support/tickets')) } catch {}
    } catch (e: any) { setInfo(e?.message || 'Invio fallito') }
  }

  const saveMonitoringConfig = async () => {
    if (!monitoring) return
    setMonitoringSaving(true)
    setMonitoringStatus('')
    const norm = (value: string | null | undefined) => {
      if (value == null) return null
      const trimmed = value.trim()
      return trimmed ? trimmed : null
    }
    try {
      const payload: any = {
        enabled: monitoring.enabled,
        prometheus: {
          enabled: monitoring.prometheus.enabled,
          pushgateway_url: norm(monitoring.prometheus.pushgateway_url ?? null),
          remote_write_url: norm(monitoring.prometheus.remote_write_url ?? null),
          job_name: norm(monitoring.prometheus.job_name ?? null),
          basic_auth_user: norm(monitoring.prometheus.basic_auth_user ?? null),
        },
        elk: {
          enabled: monitoring.elk.enabled,
          endpoint_url: norm(monitoring.elk.endpoint_url ?? null),
          index: norm(monitoring.elk.index ?? null),
          username: norm(monitoring.elk.username ?? null),
        },
      }
      if (clearPromPassword) {
        payload.prometheus.basic_auth_password = ''
      } else if (promPassword.trim()) {
        payload.prometheus.basic_auth_password = promPassword.trim()
      }
      if (clearElkPassword) {
        payload.elk.password = ''
      } else if (elkPassword.trim()) {
        payload.elk.password = elkPassword.trim()
      }
      const updated = await apiPut('/monitoring/config', payload)
      setMonitoring(updated)
      setPromPassword('')
      setElkPassword('')
      setClearPromPassword(false)
      setClearElkPassword(false)
      setMonitoringStatus('Configurazione di monitoraggio salvata')
    } catch (e: any) {
      setMonitoringStatus(e?.message || 'Salvataggio configurazione monitoraggio fallito')
    } finally {
      setMonitoringSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="font-semibold mb-2">Impostazioni</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Preferenze personali e notifiche.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold">Account</h3>
            <div className="text-sm mt-2">
              <div><span className="text-gray-500">User:</span> {me?.name || me?.email || '-'}</div>
              <div><span className="text-gray-500">Email:</span> {me?.email || '-'}</div>
              <div><span className="text-gray-500">{t('settings.version') || 'Versione'}:</span> {appVersion || '-'}</div>
            </div>
          </div>
          <div>
            <h3 className="font-semibold">Preferenze</h3>
            <div className="flex gap-2 mt-2">
              <label className="text-sm flex items-center gap-2">Lingua <select className="input" value={lang} onChange={e => setLang(e.target.value as any)}><option value="it">IT</option><option value="en">EN</option></select></label>
              <label className="text-sm flex items-center gap-2">Tema <select className="input" value={theme} onChange={e => onThemeChange(e.target.value)}><option value="auto">Auto</option><option value="light">Light</option><option value="dark">Dark</option></select></label>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold">{t('settings.monitoring.title') || 'Monitoraggio'}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.monitoring.subtitle') || 'Configura l\'invio di metriche e log verso Prometheus o stack ELK.'}</p>
          </div>
          {monitoring?.updated_at && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {(t('settings.monitoring.updated_at') || 'Aggiornato') + ': ' + new Date(monitoring.updated_at).toLocaleString()}
            </span>
          )}
        </div>
        {!monitoring ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">{monitoringStatus || t('common.loading')}</p>
        ) : (
          <div className="mt-3 space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={monitoring.enabled} onChange={onMonitoringCheckbox((checked, current) => ({ ...current, enabled: checked }))} />
                {t('settings.monitoring.enabled') || 'Abilita monitoraggio globale'}
              </label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('settings.monitoring.enabled_hint') || 'Disattiva rapidamente tutti i provider mantenendo i parametri salvati.'}
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={monitoring.prometheus.enabled} onChange={onMonitoringCheckbox((checked, current) => ({ ...current, prometheus: { ...current.prometheus, enabled: checked } }))} />
                  <span className="font-semibold">{t('settings.monitoring.prometheus') || 'Prometheus'}</span>
                </div>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.prometheus.pushgateway') || 'Pushgateway URL'}</span>
                  <input className="input" value={monitoring.prometheus.pushgateway_url || ''} onChange={onMonitoringInput((value, current) => ({ ...current, prometheus: { ...current.prometheus, pushgateway_url: value } }))} placeholder="http://prometheus-pushgateway:9091" />
                </label>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.prometheus.remote_write') || 'Remote write URL'}</span>
                  <input className="input" value={monitoring.prometheus.remote_write_url || ''} onChange={onMonitoringInput((value, current) => ({ ...current, prometheus: { ...current.prometheus, remote_write_url: value } }))} placeholder="http://prometheus:9090/api/v1/write" />
                </label>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.prometheus.job') || 'Job name'}</span>
                  <input className="input" value={monitoring.prometheus.job_name || ''} onChange={onMonitoringInput((value, current) => ({ ...current, prometheus: { ...current.prometheus, job_name: value } }))} placeholder="cp-control-plane" />
                </label>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.prometheus.user') || 'Basic auth user'}</span>
                  <input className="input" value={monitoring.prometheus.basic_auth_user || ''} onChange={onMonitoringInput((value, current) => ({ ...current, prometheus: { ...current.prometheus, basic_auth_user: value } }))} placeholder="metrics-user" />
                </label>
                <div className="text-xs flex flex-col gap-1">
                  <label>{t('settings.monitoring.prometheus.password') || 'Basic auth password'}</label>
                  <input className="input" type="password" value={promPassword} onChange={e => setPromPassword(e.target.value)} placeholder={monitoring.prometheus.basic_auth_password_set ? '••••••••' : ''} />
                  {monitoring.prometheus.basic_auth_password_set && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.monitoring.prometheus.password_set') || 'Password già configurata: lascia vuoto per mantenerla.'}</span>
                  )}
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={clearPromPassword} onChange={(e: ChangeEvent<HTMLInputElement>) => setClearPromPassword(e.target.checked)} />
                    <span>{t('settings.monitoring.prometheus.password_clear') || 'Azzera password salvata'}</span>
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={monitoring.elk.enabled} onChange={onMonitoringCheckbox((checked, current) => ({ ...current, elk: { ...current.elk, enabled: checked } }))} />
                  <span className="font-semibold">{t('settings.monitoring.elk') || 'Elastic / ELK'}</span>
                </div>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.elk.endpoint') || 'Endpoint URL'}</span>
                  <input className="input" value={monitoring.elk.endpoint_url || ''} onChange={onMonitoringInput((value, current) => ({ ...current, elk: { ...current.elk, endpoint_url: value } }))} placeholder="https://elk.example.com:9200" />
                </label>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.elk.index') || 'Indice di destinazione'}</span>
                  <input className="input" value={monitoring.elk.index || ''} onChange={onMonitoringInput((value, current) => ({ ...current, elk: { ...current.elk, index: value } }))} placeholder="cp-backups-*" />
                </label>
                <label className="text-xs flex flex-col gap-1">
                  <span>{t('settings.monitoring.elk.username') || 'Username/API key id'}</span>
                  <input className="input" value={monitoring.elk.username || ''} onChange={onMonitoringInput((value, current) => ({ ...current, elk: { ...current.elk, username: value } }))} placeholder="elastic" />
                </label>
                <div className="text-xs flex flex-col gap-1">
                  <label>{t('settings.monitoring.elk.password') || 'Password/API key'}</label>
                  <input className="input" type="password" value={elkPassword} onChange={e => setElkPassword(e.target.value)} placeholder={monitoring.elk.password_set ? '••••••••' : ''} />
                  {monitoring.elk.password_set && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.monitoring.elk.password_set') || 'Credenziale già salvata: lascia vuoto per mantenerla.'}</span>
                  )}
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={clearElkPassword} onChange={(e: ChangeEvent<HTMLInputElement>) => setClearElkPassword(e.target.checked)} />
                    <span>{t('settings.monitoring.elk.password_clear') || 'Azzera credenziale salvata'}</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <button className="btn" onClick={saveMonitoringConfig} disabled={monitoringSaving}>{monitoringSaving ? (t('common.loading') || 'Salvataggio...') : (t('settings.monitoring.save') || 'Salva monitoraggio')}</button>
              {monitoringStatus && <span className="text-sm text-gray-600 dark:text-gray-400">{monitoringStatus}</span>}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">Notifiche (local)</h3>
        <p className="text-xs text-gray-500 mb-2">Configura i contatti da avvisare in caso di errori (salvati localmente in questo browser).</p>
        <div className="flex gap-2 mb-2">
          <select className="input" value={cType} onChange={e => setCType(e.target.value as any)}>
            <option value="email">Email</option>
            <option value="telegram">Telegram</option>
            <option value="sms">SMS</option>
          </select>
          <input className="input" placeholder="indirizzo / handle / numero" value={cValue} onChange={e => setCValue(e.target.value)} />
          <button className="btn" onClick={addContact}>Aggiungi</button>
        </div>
        <ul className="text-sm">
          {contacts.map(c => (
            <li key={c.id} className="flex items-center justify-between border-t py-1">
              <span>{c.type}: {c.value}</span>
              <button className="btn-secondary" onClick={() => delContact(c.id)}>Rimuovi</button>
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">Assistenza</h3>
        <p className="text-xs text-gray-500 mb-2">Invia una richiesta di supporto: riceverai riscontro via email.</p>
        <div className="flex flex-col gap-2">
          <input className="input" placeholder="Oggetto" value={ticketSubject} onChange={e => setTicketSubject(e.target.value)} />
          <textarea className="input" placeholder="Descrizione" value={ticketMessage} onChange={e => setTicketMessage(e.target.value)} rows={4} />
          <input className="input" placeholder="Contatto (email/telefono)" value={ticketContact} onChange={e => setTicketContact(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn" onClick={submitTicket}>Invia ticket</button>
            {info && <span className="text-sm text-gray-600 dark:text-gray-400">{info}</span>}
          </div>
        </div>
        <h4 className="font-semibold mt-4 mb-2">I miei ticket</h4>
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"><th className="text-left">ID</th><th className="text-left">Oggetto</th><th>Stato</th><th>Creato</th><th></th></tr></thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{t.id}</td>
                <td>{t.subject}</td>
                <td className="text-center">{t.status}</td>
                <td className="text-center">{new Date(t.created_at).toLocaleString()}</td>
                <td className="text-right">{t.status !== 'closed' && (
                  <button className="btn-secondary" onClick={async () => { await fetch(`/support/tickets/${t.id}?status=closed`, { method: 'PATCH', credentials: 'include' }); setTickets(await apiGet('/support/tickets')) }}>Chiudi</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
